import { configureStore, Middleware } from '@reduxjs/toolkit';
import camelcaseKeys from 'camelcase-keys';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  Persistor,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import { createLogger } from 'redux-logger';
import rootReducer from 'reducers';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

const setMiddlewares = () => {
  const middlewares: Middleware[] = [];

  if (process.env.NODE_ENV === 'development') {
    const loggerOptions = {
      collapsed: true,
      titleFormatter: (action: any, time: string, took: number) => {
        return `${action.type} -> ${time} --> ${took.toFixed(2)} ms`;
      },
    };
    const logger = createLogger(loggerOptions);
    // @types/redux-logger may ship its own redux major's Middleware type;
    // the store runs on @reduxjs/toolkit's.
    middlewares.push(logger as Middleware);
  }

  // Redux 5 types a middleware's action as unknown; ours are plain actions.
  const camelcaseKeysMiddleware: Middleware = () => next => rawAction => {
    const action = rawAction as { type: string; payload?: any };
    if (action.payload && action.type.startsWith('api/')) {
      const result = camelcaseKeys(action.payload, {
        deep: true,
      });
      action.payload = result;
    }
    return next(action);
  };
  middlewares.push(camelcaseKeysMiddleware);

  return middlewares;
};

export const store = configureStore({
  reducer: rootReducer,
  devTools: process.env.NODE_ENV === 'development' ? true : false,
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(setMiddlewares()),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const persistor: Persistor = persistStore(store);
